import { z } from "zod";
import type { Operation } from "../types";

/**
 * Customer Module — the Portfolio read contract (docs/customer-module/PRD.md
 * §3.3). One GET that returns the account-rolled-up customer base; the lens
 * (Overview/Sales/Money/Support/Marketing) reshapes columns and search filters
 * rows entirely client-side (§3.4 "scope is a filter, not a re-query"), so v1
 * carries no query — the read model is the cache. moveStage/bulk land in P3.
 *
 * The DTO mirrors `@excellent/core`'s `CustomerParty` (the UI layer can't import
 * core, the entities.ts convention); the route's `customerParties()` return
 * structurally satisfies this schema.
 */

export const customerBand = z.enum(["healthy", "watch", "at_risk"]);
export const customerStage = z.enum(["prospect", "active", "at_risk", "churned"]);

const customerHealthLegs = z.object({
  engagement: z.number().nullable(),
  deals: z.number().nullable(),
  financial: z.number().nullable(),
  support: z.number().nullable(),
  attention: z.number(),
});

// Exported (RO-3-00d / #44471) so `crm/views.ts`'s renewal op can reuse this
// exact party shape instead of re-declaring 20 fields that would then drift.
// `@excellent/ui/blocks/crm/customer-layers` is already typed against the
// inferred `CustomerPartyDTO`, so the two ops stay structurally interchangeable.
export const customerPartyDTO = z.object({
  partyUrn: z.string(),
  partyKind: z.enum(["person", "organization"]),
  partyId: z.number(),
  uid: z.string(),
  name: z.string(),
  stage: customerStage,
  health: z.object({
    band: customerBand,
    score: z.number(),
    started: z.boolean(),
    legs: customerHealthLegs,
  }),
  ltv: z.object({
    lifetimeMinor: z.number(),
    currency: z.string().nullable(),
    lastTxnAt: z.string().nullable(),
  }),
  openDeals: z.number(),
  openDealValueMinor: z.number(),
  weightedPipelineMinor: z.number(),
  openTickets: z.number(),
  lastTouchAt: z.string().nullable(),
  ownerActorId: z.number().nullable(),
  contactCount: z.number(),
  dupeLeadIds: z.array(z.number()).optional(),
});
export type CustomerPartyDTO = z.infer<typeof customerPartyDTO>;

const portfolioResponse = z.object({ parties: z.array(customerPartyDTO) });
export type PortfolioResponse = z.infer<typeof portfolioResponse>;

const portfolioOp: Operation<void, void, void, PortfolioResponse> = {
  method: "GET",
  path: () => "/api/customers/portfolio",
  response: portfolioResponse,
};

// Portfolio multi-select bulk (§3.1/§3.3). v1 ships the non-destructive
// segmentation action (Add tag); owner/status/enroll/archive land as the bulk
// bar grows pickers. `ids` are party uids.
const bulkBody = z.object({
  ids: z.array(z.string()).min(1),
  action: z.enum(["add_tag"]),
  args: z.object({ tag: z.string().min(1) }).optional(),
});
export type CustomerBulkBody = z.infer<typeof bulkBody>;

const bulkResponse = z.object({ updated: z.number() });
export type CustomerBulkResponse = z.infer<typeof bulkResponse>;

const bulkOp: Operation<void, void, CustomerBulkBody, CustomerBulkResponse> = {
  method: "POST",
  path: () => "/api/customers/bulk",
  body: bulkBody,
  response: bulkResponse,
  // tags / segmentation reshape portfolio rows.
  invalidates: [portfolioOp],
};

// A01 Quick-create (§6 P1) — compose a party (org/person) + a crm_leads row (the
// commercial signal that makes it a customer), with email-based dedup. Returns
// the party uid (the /o/<uid> Dossier handle) + whether it deduped to an existing
// party rather than creating a new one.
const createBody = z.object({
  kind: z.enum(["person", "organization"]),
  name: z.string().min(1),
  email: z.string().optional(),
});
export type CustomerCreateBody = z.infer<typeof createBody>;

const createResponse = z.object({ uid: z.string(), deduped: z.boolean() });
export type CustomerCreateResponse = z.infer<typeof createResponse>;

const createOp: Operation<void, void, CustomerCreateBody, CustomerCreateResponse> = {
  method: "POST",
  path: () => "/api/customers/create",
  body: createBody,
  response: createResponse,
  // new party appears on the portfolio list.
  invalidates: [portfolioOp],
};

// A02 Log-activity (§6 P2) — write one layer-stamped activities row on the party
// (call/email/sms/meeting/note + disposition/duration/notes). It surfaces in the
// Dossier's customerTimeline spine.
const logActivityBody = z.object({
  partyUid: z.string().min(1),
  type: z.enum(["call", "email", "sms", "meeting", "note"]),
  disposition: z.string().optional(),
  durationMinutes: z.number().int().nonnegative().optional(),
  notes: z.string().optional(),
});
export type CustomerLogActivityBody = z.infer<typeof logActivityBody>;

const logActivityResponse = z.object({ ok: z.boolean() });
export type CustomerLogActivityResponse = z.infer<typeof logActivityResponse>;

const logActivityOp: Operation<void, void, CustomerLogActivityBody, CustomerLogActivityResponse> = {
  method: "POST",
  path: () => "/api/customers/activity",
  body: logActivityBody,
  response: logActivityResponse,
  // lastTouchAt (and health attention) on the portfolio party move.
  invalidates: [portfolioOp],
};

// Pre-customer guided setup (docs/design/CUSTOMERS-PRE-CUSTOMER-AUDIT.md §6) —
// the commit step of the "Build your pipeline" walk. Given the answers collected
// in the takeover (the load-bearing target → lead is already created via
// `create`), it stands up the *durable* sales environment in one pass: a primary
// ICP profile (the founder's headline artifact — also the real source for the
// `/market` ICP hub) generated from the business and overlaid with their actual
// segments/trigger/motion, plus a tracked objective from their first goal.
// Idempotent on the ICP primary — it won't fork a primary onboarding already made.
const salesSetupBody = z.object({
  /** The anchor answer — what they do / who for. Grounds the generated ICP. */
  business: z.string().optional(),
  /** Best-fit customer segments (the add-your-own chips). */
  icp: z.array(z.string()).optional(),
  /** The tailored follow-up answer (usually a buying trigger). */
  context: z.string().optional(),
  /** The chosen go-to-market motion, resolved to a label. */
  motion: z.string().optional(),
  /** The founder's first sales goal → a tracked objective. */
  goal: z.string().optional(),
  /** uid of the prospect already created from the target answer (for context). */
  targetUid: z.string().optional(),
});
export type CustomerSalesSetupBody = z.infer<typeof salesSetupBody>;

const salesSetupResponse = z.object({
  /** uid of the ICP profile created (null when there was nothing to seed it). */
  icpUid: z.string().nullable(),
  /** uid of the objective created from the goal (null when no goal was given). */
  objectiveUid: z.string().nullable(),
});
export type CustomerSalesSetupResponse = z.infer<typeof salesSetupResponse>;

const salesSetupOp: Operation<void, void, CustomerSalesSetupBody, CustomerSalesSetupResponse> = {
  method: "POST",
  path: () => "/api/customers/sales-setup",
  body: salesSetupBody,
  response: salesSetupResponse,
  // may seed pipeline/ICP context that reshapes portfolio health/stage.
  invalidates: [portfolioOp],
};

// ── RO-3-04 page READs ──────────────────────────────────────────────────────
// The Next customers mode root was a server component that called
// customerParties / forecastModel / getCustomerPropertyFacets / listPipelines
// in-process. A client route cannot, so these ops surface the same loaders
// over HTTP. Live Next call sites stay on the in-process path (PRD D6).

const nounSchema = z.object({ singular: z.string(), plural: z.string() });

const accountRowSchema = z.object({
  uid: z.string(),
  name: z.string(),
  band: customerBand,
  lifetimeMinor: z.number(),
  openDeals: z.number(),
  openDealValueMinor: z.number(),
  lastTouchAt: z.string().nullable(),
});
export type CustomerAccountRow = z.infer<typeof accountRowSchema>;

const metricsSchema = z.object({
  lifetimeMinor: z.number(),
  payingAccounts: z.number(),
  pipelineMinor: z.number(),
  openDealCount: z.number(),
  atRiskCount: z.number(),
  atRiskLifetimeMinor: z.number(),
});
export type CustomerPortfolioMetrics = z.infer<typeof metricsSchema>;

const urgentSchema = z.object({
  name: z.string(),
  lifetimeMinor: z.number(),
  openDealValueMinor: z.number(),
  lastTouchAt: z.string().nullable(),
});
export type CustomerUrgentAccount = z.infer<typeof urgentSchema>;

// ScopedTask is a large shared type; z.custom mirrors personal.item / modes
// leaves that thread opaque task rows across the seam without redeclaring
// every field.
const scopedTaskSchema = z.custom<Record<string, unknown>>(
  (v) => v != null && typeof v === "object",
);

const dashboardResponse = z.object({
  noun: nounSchema,
  started: z.boolean(),
  newDealHref: z.string(),
  rows: z.array(accountRowSchema),
  metrics: metricsSchema,
  urgent: urgentSchema.nullable(),
  tasks: z.array(scopedTaskSchema),
});
export type CustomersDashboardResponse = z.infer<typeof dashboardResponse>;

const dashboardOp: Operation<void, void, void, CustomersDashboardResponse> = {
  method: "GET",
  path: () => "/api/customers/dashboard",
  response: dashboardResponse,
};

// forecastModel() shape is deep; z.custom accepts the structural return and
// the route validates the non-empty envelope by construction.
const forecastResponse = z.object({
  model: z.custom<Record<string, unknown>>((v) => v != null && typeof v === "object"),
});
export type CustomersForecastResponse = z.infer<typeof forecastResponse>;

const forecastOp: Operation<void, void, void, CustomersForecastResponse> = {
  method: "GET",
  path: () => "/api/customers/forecast",
  response: forecastResponse,
};

const propertyFacetCustomer = z.object({
  uid: z.string(),
  name: z.string(),
  stage: z.enum(["touched", "paying"]),
  revenueMinor: z.number(),
});
const propertyFacet = z.object({
  slug: z.string(),
  name: z.string(),
  revenueMinor: z.number(),
  payingCustomers: z.number(),
  customers: z.array(propertyFacetCustomer),
});
const propertyFacetsResponse = z.object({
  noun: nounSchema,
  properties: z.array(propertyFacet),
  propertyCount: z.number(),
  attributedCustomers: z.number(),
  totalRevenueMinor: z.number(),
  totalPayingCustomers: z.number(),
});
export type CustomersPropertyFacetsResponse = z.infer<typeof propertyFacetsResponse>;

const propertyFacetsOp: Operation<void, void, void, CustomersPropertyFacetsResponse> = {
  method: "GET",
  path: () => "/api/customers/property-facets",
  response: propertyFacetsResponse,
};

const boardQuery = z.object({
  pipeline: z.string().optional(),
});
const boardStage = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  is_won: z.number(),
  is_lost: z.number(),
  probability_pct: z.number().nullable(),
});
const boardDeal = z.object({
  id: z.number(),
  uid: z.string(),
  name: z.string(),
  account: z.string().nullable(),
  valueMinor: z.number().nullable(),
  currency: z.string().nullable(),
  prob: z.number().nullable(),
  owner: z.string().nullable(),
  stageId: z.number(),
  ageDays: z.number(),
});
const boardResponse = z.object({
  empty: z.boolean(),
  title: z.string().optional(),
  pipelines: z.array(z.object({ slug: z.string(), name: z.string() })),
  selectedSlug: z.string().optional(),
  stages: z.array(boardStage).optional(),
  deals: z.array(boardDeal).optional(),
});
export type CustomersBoardResponse = z.infer<typeof boardResponse>;

const boardOp: Operation<void, z.infer<typeof boardQuery>, void, CustomersBoardResponse> = {
  method: "GET",
  path: () => "/api/customers/board",
  query: boardQuery,
  response: boardResponse,
};

export const customers = {
  portfolio: portfolioOp,
  bulk: bulkOp,
  create: createOp,
  logActivity: logActivityOp,
  salesSetup: salesSetupOp,
  dashboard: dashboardOp,
  forecast: forecastOp,
  propertyFacets: propertyFacetsOp,
  board: boardOp,
};

/** Ops this port mounts (for registration + differential tests). */
export const CUSTOMERS_READ_OPS = [
  "portfolio",
  "dashboard",
  "forecast",
  "propertyFacets",
  "board",
] as const;
export const CUSTOMERS_MUTATE_OPS = [
  "bulk",
  "create",
  "logActivity",
  "salesSetup",
] as const;
